import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { Alert, Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { END_POINT } from "../../../constants/defaultValues";
import { AddCommaNumber } from "../../../helpers/Utils";
import { isC, isD, isOwnerOrAdminUser, isU } from "../../../helpers/Roles";

const statusLabels = {
  draft: "Nháp",
  pending_approval: "Chờ duyệt",
  posted: "Đã nhập kho",
  rejected: "Từ chối",
  reversed: "Đã hoàn tác",
};

const eventLabels = {
  created: "Tạo phiếu",
  updated: "Sửa phiếu",
  submitted: "Gửi duyệt",
  approved: "Duyệt và nhập kho",
  auto_approved: "Tự động duyệt và nhập kho",
  rejected: "Từ chối",
  reversed: "Hoàn tác",
};

const emptyItem = () => ({ stock_id: "", stock_name: "", unit: "", quantity: "", unit_price: "", expiry_date: "", batch_code: "", storage_location: "", note: "" });
const localDate = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const emptyForm = () => ({
  transaction_date: localDate(),
  warehouse_name: "Kho chính", provider_id: "", paid_amount: "0", notes: "",
  items: [emptyItem()],
});
const money = (value) => `${new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(Number(value || 0))} đ`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
const apiError = (error) => error.response?.data?.responseText || "Không thể xử lý phiếu nhập. Vui lòng thử lại.";

// Keep the same ID and payload after an uncertain network result. A retry can then
// only retrieve the original draft; editing must wait for a known response.
const StockInWorkspace = ({ stocks = [], onLegacy, reloadStocks }) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(() => new URLSearchParams(window.location.search).get("action") === "create");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [uncertain, setUncertain] = useState(false);
  const requestId = useRef(uuidv4());
  const pendingAction = useRef(false);
  const owner = isOwnerOrAdminUser();
  const isOwnDraft = selected && Number(selected.createdBy) === Number(localStorage.getItem("user_id"));

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${END_POINT}/stock-in`, {
        ...auth(), params: { pageSize: 20, currentPage: page, status: statusFilter || undefined, search: search.trim() || undefined },
      });
      setRows(response.data.data || []);
      setTotalPages(response.data.totalPage || 1);
      setTotalItems(response.data.totalItem || 0);
      setMessage((current) => current?.kind === "danger" ? null : current);
    } catch (error) {
      setMessage({ kind: "danger", text: apiError(error) });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setSelected(null); return null; }
    try {
      const response = await axios.get(`${END_POINT}/stock-in/${id}`, auth());
      setSelected(response.data.data);
      return response.data.data;
    } catch (error) {
      setMessage({ kind: "danger", text: apiError(error) });
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    axios.get(`${END_POINT}/company-provider`, auth())
      .then((response) => setProviders(response.data.data || []))
      .catch(() => setProviders([]));
  }, []);

  const resetForm = () => {
    requestId.current = uuidv4();
    setForm(emptyForm());
    setEditingId(null);
    setUncertain(false);
    setEditing(true);
    setMessage(null);
  };

  const editDraft = (document) => {
    if (!document || document.status !== "draft") return;
    requestId.current = document.requestId;
    setEditingId(document.id);
    setForm({
      transaction_date: document.transactionDate,
      warehouse_name: document.warehouseName || "Kho chính",
      provider_id: document.providerId || "",
      paid_amount: String(document.paidAmount || 0),
      notes: document.notes || "",
      items: (document.items || []).map((item) => ({
        stock_id: item.stockId || "", stock_name: item.stockId ? "" : item.stockNameSnapshot,
        unit: item.stockId ? "" : item.unitSnapshot,
        quantity: String(item.quantity), unit_price: String(item.unitPrice),
        expiry_date: item.expiryDate || "", batch_code: item.batchCode || "",
        storage_location: item.storageLocation || "", note: item.note || "",
      })),
    });
    setUncertain(false);
    setEditing(true);
    setMessage(null);
  };

  const changeItem = (index, field, value) => {
    setForm((current) => ({ ...current, items: current.items.map((item, i) =>
      i === index ? { ...item, [field]: value, ...(field === "stock_id" ? { stock_name: "", unit: "" } : {}) } : item) }));
  };

  const payload = () => ({
    request_id: requestId.current,
    transaction_date: form.transaction_date,
    warehouse_name: form.warehouse_name.trim(),
    provider_id: form.provider_id || null,
    paid_amount: form.paid_amount,
    notes: form.notes,
    items: form.items.map((item) => ({
      ...(item.stock_id ? { stock_id: Number(item.stock_id) } : { stock_name: item.stock_name.trim(), unit: item.unit.trim() }),
      quantity: item.quantity, unit_price: item.unit_price,
      expiry_date: item.expiry_date || null, batch_code: item.batch_code || null,
      storage_location: item.storage_location || null, note: item.note || null,
    })),
  });

  const save = async (submit) => {
    if (pendingAction.current) return;
    if (!form.transaction_date || !form.warehouse_name.trim() || !form.items.length || form.items.some((item) =>
      (!item.stock_id && (!item.stock_name.trim() || !item.unit.trim())) ||
      !/^\d+(?:\.\d{1,3})?$/.test(item.quantity) || Number(item.quantity) <= 0 ||
      !/^\d+(?:\.\d{1,2})?$/.test(item.unit_price) || Number(item.unit_price) <= 0)) {
      setMessage({ kind: "danger", text: "Điền kho, hàng hóa, số lượng và đơn giá hợp lệ cho từng dòng." });
      return;
    }
    pendingAction.current = true;
    setBusy(true);
    try {
      const response = editingId
        ? await axios.put(`${END_POINT}/stock-in/${editingId}`, payload(), auth())
        : await axios.post(`${END_POINT}/stock-in`, payload(), auth());
      const id = response.data.data.id;
      setEditingId(id);
      setUncertain(false);
      if (submit) {
        const submitted = await axios.post(`${END_POINT}/stock-in/${id}/submit`, {}, auth());
        setMessage({ kind: "success", text: submitted.data.data?.status === "posted" ? "Đã nhập kho và cập nhật tồn kho." : "Đã gửi phiếu để duyệt. Tồn kho chưa thay đổi." });
        if (submitted.data.data?.status === "posted") reloadStocks();
        setEditing(false);
      } else {
        setMessage({ kind: "success", text: "Đã lưu bản nháp. Tồn kho chưa thay đổi." });
        setEditing(false);
      }
      await loadDetail(id);
      await refresh();
    } catch (error) {
      const noResponse = !error.response;
      if (noResponse) setUncertain(true);
      setMessage({ kind: "danger", text: noResponse
        ? "Chưa xác nhận được kết quả. Giữ nguyên phiếu và bấm Lưu lại để kiểm tra; không sửa dữ liệu lúc này."
        : apiError(error) });
      if (error.response?.status === 409) {
        await refresh();
        if (editingId) await loadDetail(editingId);
      }
    } finally {
      pendingAction.current = false;
      setBusy(false);
    }
  };

  const act = async (action, document, reason) => {
    if (pendingAction.current) return;
    pendingAction.current = true;
    setBusy(true);
    try {
      await axios.post(`${END_POINT}/stock-in/${document.id}/${action}`, reason ? { reason } : {}, auth());
      if (action === "approve" || action === "reverse") reloadStocks();
      setMessage({ kind: "success", text: action === "approve" ? "Đã nhập kho và cập nhật tồn kho." : action === "submit" ? "Đã gửi duyệt." : action === "reject" ? "Đã từ chối phiếu." : "Đã hoàn tác phiếu." });
      await loadDetail(document.id);
      await refresh();
    } catch (error) {
      setMessage({ kind: "danger", text: apiError(error) });
      if (error.response?.status === 409) { await loadDetail(document.id); await refresh(); }
    } finally {
      pendingAction.current = false;
      setBusy(false);
    }
  };

  const withReason = (action) => {
    const reason = window.prompt(action === "reject" ? "Lý do từ chối phiếu" : "Lý do hoàn tác phiếu");
    if (reason === null) return;
    if (!reason.trim()) { setMessage({ kind: "danger", text: "Cần nhập lý do." }); return; }
    act(action, selected, reason.trim());
  };

  const removeDraft = async () => {
    if (!selected || !window.confirm(`Xóa phiếu nháp ${selected.code}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${END_POINT}/stock-in/${selected.id}`, auth());
      setSelected(null);
      setMessage({ kind: "success", text: "Đã xóa phiếu nháp." });
      await refresh();
    } catch (error) {
      setMessage({ kind: "danger", text: apiError(error) });
      if (error.response?.status === 409) { await loadDetail(selected.id); await refresh(); }
    } finally { setBusy(false); }
  };

  const pagePostedAmount = rows.filter((row) => row.status === "posted")
    .reduce((sum, row) => sum + Number(row.totalAmount || 0), 0);
  const formTotal = form.items.reduce((sum, item) =>
    sum + Math.round(Number(item.quantity || 0) * Number(item.unit_price || 0) * 100) / 100, 0);

  return <div className="warehouse-import-modern-page">
    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
      <div><h1 className="mb-1">Nhập kho hằng ngày</h1><p className="text-muted mb-0">Phiếu nhiều mặt hàng, duyệt nhập và theo dõi từng thay đổi.</p></div>
      <div className="d-flex align-items-center mt-2">
        <Button color="secondary" outline onClick={onLegacy} disabled={uncertain} className="mr-2">Phiếu nhập trước đây</Button>
        <Button color="primary" onClick={resetForm} disabled={!isC("warehouseImport") || busy || uncertain}>+ Tạo phiếu nhập</Button>
      </div>
    </div>
    {message && <Alert color={message.kind}>{message.text}</Alert>}
    <div className="warehouse-import-summary-grid mb-3">
      <div className="warehouse-import-summary-card"><div><span className="warehouse-import-summary-label">Giá trị đã nhập</span><strong>{money(pagePostedAmount)}</strong><small>Phiếu đã nhập trên trang hiện tại</small></div></div>
      <div className="warehouse-import-summary-card"><div><span className="warehouse-import-summary-label">Số phiếu</span><strong>{AddCommaNumber(totalItems)}</strong><small>Theo bộ lọc đang chọn</small></div></div>
    </div>
    {editing && <Card className="mb-3"><CardBody>
      <div className="d-flex justify-content-between"><h2 className="h4">{editingId ? "Sửa phiếu nháp" : "Phiếu nhập mới"}</h2><Button color="link" onClick={() => setEditing(false)} disabled={busy || uncertain}>Đóng</Button></div>
      <Row>
        <Col md="3"><FormGroup><Label>Ngày nhập</Label><Input type="date" value={form.transaction_date} disabled={busy || uncertain} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} /></FormGroup></Col>
        <Col md="3"><FormGroup><Label>Kho</Label><Input value={form.warehouse_name} disabled={busy || uncertain} maxLength="255" onChange={(e) => setForm({ ...form, warehouse_name: e.target.value })} /></FormGroup></Col>
        <Col md="3"><FormGroup><Label>Nhà cung cấp</Label><Input type="select" value={form.provider_id} disabled={busy || uncertain} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}><option value="">Không chọn</option>{providers.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}</Input></FormGroup></Col>
        <Col md="3"><FormGroup><Label>Đã trả nhà cung cấp</Label><Input type="number" min="0" step="0.01" value={form.paid_amount} disabled={busy || uncertain} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} /></FormGroup></Col>
      </Row>
      <FormGroup><Label>Ghi chú phiếu</Label><Input value={form.notes} maxLength="5000" disabled={busy || uncertain} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormGroup>
      <div className="table-responsive"><table className="table table-bordered mb-2"><thead><tr><th style={{ minWidth: 200 }}>Hàng hóa</th><th style={{ minWidth: 130 }}>Đơn vị mới</th><th style={{ minWidth: 100 }}>Số lượng</th><th style={{ minWidth: 120 }}>Đơn giá</th><th style={{ minWidth: 120 }}>Thành tiền</th><th style={{ minWidth: 145 }}>Hạn dùng</th><th style={{ minWidth: 130 }}>Mã lô</th><th style={{ minWidth: 130 }}>Vị trí</th><th /></tr></thead><tbody>
        {form.items.map((item, index) => {
          const otherSelected = new Set(
            form.items
              .filter((_, i) => i !== index && _.stock_id)
              .map((_) => String(_.stock_id))
          );
          const availableStocks = stocks.filter(
            (stock) =>
              (!stock.warehouse_name || stock.warehouse_name === form.warehouse_name) &&
              (!otherSelected.has(String(stock.id)) || String(stock.id) === String(item.stock_id))
          );
          return (
            <tr key={index}>
              <td><Input type="select" value={item.stock_id} disabled={busy || uncertain} onChange={(e) => changeItem(index, "stock_id", e.target.value)}><option value="">+ Hàng mới</option>{availableStocks.map((stock) => <option key={stock.id} value={stock.id}>{stock.name} ({stock.unit})</option>)}</Input>{!item.stock_id && <Input className="mt-1" placeholder="Tên hàng mới" maxLength="255" value={item.stock_name} disabled={busy || uncertain} onChange={(e) => changeItem(index, "stock_name", e.target.value)} />}</td>
          <td>{!item.stock_id && <Input placeholder="Đơn vị" maxLength="255" value={item.unit} disabled={busy || uncertain} onChange={(e) => changeItem(index, "unit", e.target.value)} />}</td>
          <td><Input type="number" min="0.001" step="0.001" value={item.quantity} disabled={busy || uncertain} onChange={(e) => changeItem(index, "quantity", e.target.value)} /></td>
          <td><Input type="number" min="0.01" step="0.01" value={item.unit_price} disabled={busy || uncertain} onChange={(e) => changeItem(index, "unit_price", e.target.value)} /></td>
          <td>{money(Math.round(Number(item.quantity || 0) * Number(item.unit_price || 0) * 100) / 100)}</td>
          <td><Input type="date" value={item.expiry_date} disabled={busy || uncertain} onChange={(e) => changeItem(index, "expiry_date", e.target.value)} /></td>
          <td><Input value={item.batch_code} maxLength="255" disabled={busy || uncertain} onChange={(e) => changeItem(index, "batch_code", e.target.value)} /></td>
          <td><Input value={item.storage_location} maxLength="255" disabled={busy || uncertain} onChange={(e) => changeItem(index, "storage_location", e.target.value)} /></td>
          <td><Button color="danger" outline size="sm" disabled={busy || uncertain || form.items.length === 1} onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== index) })}>Xóa</Button></td>
        </tr>
      );
    })}</tbody></table></div>
      <Button color="secondary" outline size="sm" disabled={busy || uncertain || form.items.length >= 200} onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}>+ Thêm dòng</Button>
      <strong className="float-right">Tổng phiếu dự tính: {money(formTotal)}</strong>
      <div className="text-right mt-3"><Button color="secondary" outline className="mr-2" disabled={busy} onClick={() => save(false)}>{busy ? "Đang lưu..." : "Lưu nháp"}</Button><Button color="primary" disabled={busy} onClick={() => save(true)}>{busy ? "Đang xử lý..." : "Lưu và gửi duyệt"}</Button></div>
    </CardBody></Card>}
    <Card><CardBody>
      <div className="d-flex align-items-center mb-3 flex-wrap">
        <Input type="select" style={{ maxWidth: 200 }} value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}><option value="">Tất cả trạng thái</option>{Object.entries(statusLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</Input>
        <Input className="ml-2" style={{ maxWidth: 260 }} placeholder="Tìm theo mã phiếu" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
        <Button color="secondary" outline className="ml-2" disabled={loading} onClick={refresh}>Tải lại</Button>
      </div>
      {loading && <p>Đang tải phiếu nhập...</p>}
      <div className="table-responsive"><table className="table table-hover"><thead><tr><th>Mã phiếu</th><th>Ngày</th><th>Kho</th><th>Giá trị</th><th>Trạng thái</th><th /></tr></thead><tbody>
        {rows.map((row) => <tr key={row.id}><td>{row.code}</td><td>{row.transactionDate}</td><td>{row.warehouseName}</td><td>{money(row.totalAmount)}</td><td>{statusLabels[row.status] || row.status}</td><td><Button color="link" onClick={() => loadDetail(row.id)}>Chi tiết</Button></td></tr>)}
      </tbody></table></div>
      {!loading && rows.length === 0 && <p className="text-muted">Chưa có phiếu nhập trong bộ lọc này.</p>}
      <div className="d-flex justify-content-between align-items-center"><span>{totalItems} phiếu</span><div><Button color="secondary" outline size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Trước</Button><span className="mx-2">{page}/{totalPages}</span><Button color="secondary" outline size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Sau</Button></div></div>
    </CardBody></Card>
    {selected && <Card className="mt-3"><CardBody>
      <div className="d-flex justify-content-between"><h2 className="h4">{selected.code} · {statusLabels[selected.status]}</h2><Button color="link" onClick={() => setSelected(null)}>Đóng</Button></div>
      <p>Ngày: {selected.transactionDate} · Kho: {selected.warehouseName} · Tổng tiền: {money(selected.totalAmount)} · Đã trả: {money(selected.paidAmount)}</p>
      {selected.notes && <p>Ghi chú: {selected.notes}</p>}
      {(selected.rejectionReason || selected.reversalReason) && <Alert color="warning">{selected.rejectionReason || selected.reversalReason}</Alert>}
      <div className="table-responsive"><table className="table"><thead><tr><th>Hàng hóa</th><th>Số lượng</th><th>Đơn giá</th><th>Thành tiền</th><th>Lô / hạn dùng</th></tr></thead><tbody>{(selected.items || []).map((item) => <tr key={item.id}><td>{item.stockNameSnapshot} ({item.unitSnapshot})</td><td>{item.quantity} {item.unitSnapshot}</td><td>{money(item.unitPrice)}</td><td>{money(item.lineTotal)}</td><td>{item.batchCode || "—"} / {item.expiryDate || "—"}</td></tr>)}</tbody></table></div>
      <div className="mb-3">
        {selected.status === "draft" && <><Button color="primary" outline className="mr-2" disabled={!isOwnDraft || !isU("warehouseImport") || busy} onClick={() => editDraft(selected)}>Sửa nháp</Button><Button color="success" className="mr-2" disabled={!isOwnDraft || !isC("warehouseImport") || busy} onClick={() => act("submit", selected)}>Gửi duyệt</Button><Button color="danger" outline disabled={!isOwnDraft || !isD("warehouseImport") || busy} onClick={removeDraft}>Xóa nháp</Button></>}
        {selected.status === "pending_approval" && owner && <><Button color="success" className="mr-2" disabled={busy} onClick={() => act("approve", selected)}>Duyệt và nhập kho</Button><Button color="danger" outline disabled={busy} onClick={() => withReason("reject")}>Từ chối</Button></>}
        {selected.legacySourceType === "warehouse_import" && <Alert color="info">Phiếu lịch sử chỉ dùng để tra cứu. Cần đối soát trước khi điều chỉnh tồn kho.</Alert>}
        {selected.status === "posted" && selected.legacySourceType !== "warehouse_import" && owner && <Button color="warning" outline disabled={busy} onClick={() => withReason("reverse")}>Hoàn tác nhập kho</Button>}
      </div>
      <h3 className="h5">Nhật ký phiếu</h3>
      <ul className="list-unstyled mb-0">{(selected.events || []).map((event) => <li key={event.id} className="border-top py-2"><strong>{eventLabels[event.eventType] || event.eventType}</strong> · {new Date(event.createdAt).toLocaleString("vi-VN")}{event.reason && <div>Lý do: {event.reason}</div>}</li>)}</ul>
    </CardBody></Card>}
  </div>;
};

export default StockInWorkspace;
